import { useState, useEffect, useCallback, forwardRef, useRef } from 'react';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import debounce from 'lodash/debounce';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';

import { Modal, ModalMethods, Text, Loader, toasts } from '../../../../ui-component';
import { api } from '../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../utils/common';
import EditCategoryNameModal from './EditCategoryNameModal';

export type CategoryType = {
  id: number;
  name: string;
  boutique_id: number;
};

type CategorySelectorModalProps = {
  onCategorySelect: (categoryName: string) => void;
  selectedCategory?: string;
};

const CategorySelectorModal = forwardRef<ModalMethods, CategorySelectorModalProps>(
  ({ onCategorySelect, selectedCategory }, ref) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [categories, setCategories] = useState<CategoryType[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [categoryToEdit, setCategoryToEdit] = useState<CategoryType | null>(null);

    const editCategoryModalRef = useRef<ModalMethods>(null);

    const fetchCategories = useCallback(async (query: string = '') => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsLoading(true);

      try {
        const encodedQuery = encodeURIComponent(query.trim());
        const response = await api.getRequest(
          `custom_outfit/category_suggestions?query=${encodedQuery}`
        );

        if (response.status && !_isNil(response.data)) {
          const categoriesData = Array.isArray(response.data) ? response.data : [];

          // Filter out categories with empty names
          const validCategories = categoriesData.filter(
            (cat: CategoryType) => cat.name && !_isEmpty(cat.name.trim())
          );

          setCategories(validCategories);
        } else {
          toasts(
            'error',
            response.message || 'Failed to fetch categories',
            'fetch-categories-error'
          );
          setCategories([]);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'fetch-categories-error');
        }
        setCategories([]);
      } finally {
        setIsLoading(false);
      }
    }, []);

    const debouncedFetchCategoriesRef = useRef(
      debounce((query: string) => {
        void fetchCategories(query);
      }, 500)
    );

    const prevSearchQueryRef = useRef<string>('');
    const isInitialMountRef = useRef(true);

    useEffect(() => {
      const trimmedQuery = searchQuery.trim();
      const prevTrimmedQuery = prevSearchQueryRef.current.trim();

      if (_isEmpty(trimmedQuery)) {
        if (!_isEmpty(prevTrimmedQuery)) {
          debouncedFetchCategoriesRef.current.cancel();
        }

        if (isInitialMountRef.current || !_isEmpty(prevTrimmedQuery)) {
          void fetchCategories('');
        }
      } else {
        debouncedFetchCategoriesRef.current(trimmedQuery);
      }

      prevSearchQueryRef.current = searchQuery;
      isInitialMountRef.current = false;
    }, [searchQuery, fetchCategories]);

    useEffect(() => {
      return () => {
        debouncedFetchCategoriesRef.current.cancel();
      };
    }, []);

    const handleCategoryClick = (category: CategoryType) => {
      onCategorySelect(category.name);

      if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
        ref.current.hide();
      }
    };

    const handleEditCategoryClick = (category: CategoryType) => {
      setCategoryToEdit(category);
      editCategoryModalRef.current?.show();
    };

    const handleDeleteCategoryClick = async (category: CategoryType) => {
      const confirmed = window.confirm(
        'Are you sure you want to delete this category? This action cannot be undone.'
      );

      if (!confirmed) {
        return;
      }

      try {
        const response = await api.putRequest(`custom_outfit/category/delete/${category.id}`, {});

        if (response.status) {
          toasts('success', 'Category deleted successfully', 'category-delete-success');
          setCategories((prev) => prev.filter((cat) => cat.id !== category.id));

          if (selectedCategory === category.name) {
            onCategorySelect('');
          }
        } else {
          toasts('error', response.message || 'Failed to delete category', 'category-delete-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'category-delete-error');
        }
      }
    };

    const handleCreateCategory = async () => {
      const trimmedQuery = searchQuery.trim();

      if (_isEmpty(trimmedQuery)) {
        toasts('error', 'Please enter a category name', 'category-name-required');

        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      setIsCreating(true);

      try {
        const encodedQuery = encodeURIComponent(trimmedQuery);
        const response = await api.postRequest(`custom_outfit/category?query=${encodedQuery}`, {});

        if (response.status && !_isNil(response.data)) {
          const newCategory = response.data;
          onCategorySelect(newCategory.name || trimmedQuery);

          if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
            ref.current.hide();
          }

          toasts('success', 'Category created successfully', 'category-created-success');
        } else {
          toasts('error', response.message || 'Failed to create category', 'create-category-error');
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'create-category-error');
        }
      } finally {
        setIsCreating(false);
      }
    };

    const isQueryInCategories = categories.some(
      (cat) => cat.name.toLowerCase() === searchQuery.trim().toLowerCase()
    );

    const showCreateButton = !_isEmpty(searchQuery.trim()) && !isQueryInCategories;

    return (
      <Modal
        ref={ref}
        size="large"
        title="Select existing or add new piece"
        showFooter={false}
        showCloseIcon={true}
      >
        <div className="flex flex-col h-full min-h-[400px]">
          <div className="mb-1">
            <Text className="font-inter text-sm text-gray-600 mb-4 mt-1 block" color="black">
              Select any piece to import all its details. You can rename or edit it anytime.
            </Text>
            <div className="relative flex items-center w-full">
              <input
                type="text"
                placeholder="Search or type a new category"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-10 px-4 border border-gray-300 rounded-lg text-sm font-inter outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-500 placeholder:text-gray-500"
                autoFocus
              />
            </div>
          </div>

          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <Loader />
            </div>
          ) : (
            <>
              {categories.length > 0 ? (
                <div className="flex flex-col gap-2 py-2 max-h-[300px] overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
                  {categories.map((category) => {
                    const isSelected = selectedCategory === category.name;

                    return (
                      <div
                        key={category.id}
                        onClick={() => handleCategoryClick(category)}
                        className={`px-4 py-3 rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex items-center justify-between gap-3 ${
                          isSelected
                            ? 'bg-blue-50 border border-blue-500'
                            : 'border border-transparent'
                        }`}
                      >
                        <Text size="medium" color="black" className="font-inter truncate">
                          {category.name}
                        </Text>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleEditCategoryClick(category);
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            aria-label="Edit category name"
                          >
                            <FaPencilAlt size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              void handleDeleteCategoryClick(category);
                            }}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            aria-label="Delete category"
                          >
                            <MdDelete size={18} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center justify-center h-[200px] p-5">
                  <Text color="gray" size="small">
                    {searchQuery
                      ? 'No categories found matching your search'
                      : 'No categories available'}
                  </Text>
                </div>
              )}
            </>
          )}

          {showCreateButton && (
            <div className="mt-auto pb-5 border-t border-gray-300">
              <button
                type="button"
                onClick={handleCreateCategory}
                disabled={isCreating}
                className="w-full px-4 py-4 bg-blue-600 text-white rounded-lg font-semibold transition-all hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isCreating ? (
                  <>
                    <Loader />
                    <Text size="small" color="white">
                      Creating...
                    </Text>
                  </>
                ) : (
                  <Text
                    color="white"
                    className="font-inter font-medium text-sm leading-none tracking-normal"
                  >
                    + Create &quot;{searchQuery.trim()}&quot; as new Category
                  </Text>
                )}
              </button>
            </div>
          )}
        </div>

        <EditCategoryNameModal
          ref={editCategoryModalRef}
          category={categoryToEdit}
          onSaved={(updatedName: string) => {
            if (categoryToEdit && selectedCategory === categoryToEdit.name) {
              onCategorySelect(updatedName);
            }

            void fetchCategories(searchQuery);
          }}
        />
      </Modal>
    );
  }
);

CategorySelectorModal.displayName = 'CategorySelectorModal';

export default CategorySelectorModal;
